import React, { useCallback, useMemo } from 'react'
import { Box, Button, Flex, Text } from '@workwhile/ui'
import { CircleMinus } from 'lucide-react'
import { MatchData, Searcher } from 'fast-fuzzy'
import { ModalAsyncSelect } from 'components/ModalSelect'
import { useCompanyGroupsQuery } from 'queries/companyGroups'
import {
  CompanyScopesEditorProps,
  ScopeOption,
  ScopeDatesFields,
  createScopeDates,
  removeScopeAt,
  scopeFuzzyOptions,
  updateScopeAt,
} from './shared'

export const CompanyScopesCompanyGroupsSection = ({
  value,
  onChange,
  existingScopes = [],
  errors,
}: CompanyScopesEditorProps) => {
  const { data: companyGroups } = useCompanyGroupsQuery()

  const existingCompanyGroupIds = useMemo(
    () =>
      new Set(
        existingScopes
          .map((scope) => scope.companyGroupId)
          .filter((id): id is number => id != null)
      ),
    [existingScopes]
  )

  const selectedCompanyGroupIds = useMemo(
    () =>
      new Set(
        value
          .filter((scope) => scope.scopeType === 'company_group')
          .map((scope) => scope.companyGroupId)
          .filter((id): id is number => id != null)
      ),
    [value]
  )

  const companyGroupOptions = useMemo(() => {
    return (
      companyGroups
        ?.filter(
          (group) =>
            !existingCompanyGroupIds.has(group.id) &&
            !selectedCompanyGroupIds.has(group.id)
        )
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((group) => ({
          label: `${group.name} (${group.id})`,
          value: group.id,
        })) ?? []
    )
  }, [companyGroups, existingCompanyGroupIds, selectedCompanyGroupIds])

  const companyGroupSearcher = useMemo(() => {
    return new Searcher(companyGroupOptions, scopeFuzzyOptions)
  }, [companyGroupOptions])

  const loadCompanyGroupOptions = useCallback(
    (rawInput: string) => {
      if (!rawInput) return Promise.resolve(companyGroupOptions)
      const results = companyGroupSearcher.search(
        rawInput,
        scopeFuzzyOptions
      ) as unknown as MatchData<ScopeOption>[]
      return Promise.resolve(results)
    },
    [companyGroupOptions, companyGroupSearcher]
  )

  return (
    <Box
      p={2}
      border="1px solid"
      borderColor="neutrals.100"
      borderRadius="small"
    >
      <Text fontWeight="medium" mb={2}>
        Company groups
      </Text>
      <ModalAsyncSelect
        block
        isClearable
        isSearchable
        placeholder="Add a company group"
        defaultOptions={companyGroupOptions}
        loadOptions={loadCompanyGroupOptions}
        value={null}
        onChange={(option) => {
          if (!option) return
          onChange([
            ...value,
            {
              scopeType: 'company_group',
              companyId: null,
              companyName: null,
              companyGroupId: option.value,
              companyGroupName: option.label,
              ...createScopeDates(),
            },
          ])
        }}
        aria-label="Add company group scope"
      />
      <Flex flexDirection="column" gap={2} mt={3}>
        {value.map((scope, index) => {
          if (scope.scopeType !== 'company_group') {
            return null
          }
          return (
            <Box
              key={`company-group-${scope.companyGroupId}-${index}`}
              p={2}
              border="1px solid"
              borderColor="neutrals.50"
              borderRadius="small"
            >
              <Flex justifyContent="space-between" alignItems="center">
                <Text fontWeight="medium">
                  {scope.companyGroupName ??
                    `Company group ${scope.companyGroupId}`}
                </Text>
                <Button
                  variant="text"
                  kind="small"
                  color="red"
                  onClick={() => onChange(removeScopeAt(value, index))}
                  aria-label="Remove company group scope"
                >
                  <CircleMinus size={16} />
                </Button>
              </Flex>
              <ScopeDatesFields
                scope={scope}
                index={index}
                errors={errors}
                onChange={(next) => onChange(updateScopeAt(value, index, next))}
              />
            </Box>
          )
        })}
      </Flex>
    </Box>
  )
}
